/**
 * Premium UI click sound — synthesized via Web Audio API.
 * ~80ms digital/tactile click at low volume.
 * No external audio file required.
 */

let audioCtx = null;

function getContext() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtx;
}

/** Play a short, premium digital click. */
export function playClick() {
  try {
    const ctx = getContext();
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;

    // Master gain — keep volume very low (12%)
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.12, now);
    master.connect(ctx.destination);

    // Layer 1: crisp high-frequency tick (subtle digital transient)
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(4200, now);
    osc.frequency.exponentialRampToValueAtTime(1800, now + 0.04);
    oscGain.gain.setValueAtTime(0.6, now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    osc.connect(oscGain);
    oscGain.connect(master);
    osc.start(now);
    osc.stop(now + 0.07);

    // Layer 2: very short noise burst for tactile texture
    const bufferSize = Math.floor(ctx.sampleRate * 0.03); // 30ms
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize); // fade out
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    // Bandpass filter to keep noise tight and clean
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 6000;
    filter.Q.value = 1.2;

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.25, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(master);
    noise.start(now);
    noise.stop(now + 0.05);
  } catch (_) {
    // Sound is non-critical — fail silently
  }
}

// Selectors that qualify as "interactive controls"
const INTERACTIVE = [
  'button',
  'a[role="button"]',
  '[data-testid*="btn"]',
  '[data-testid*="tab-"]',
  '[data-testid="floating-nav-toggle"]',
  '[data-testid*="logo-"]',
  '[data-testid="footer-tapelligence-logo"]',
].join(',');

/**
 * Attach a single global click listener.
 * Call once in App.js useEffect.
 * Returns a cleanup function.
 */
export function initClickSound() {
  const handler = (e) => {
    // Walk up from the click target to find an interactive ancestor
    const el = e.target.closest(INTERACTIVE);
    if (!el) return;

    // Skip text-only links that aren't styled as buttons
    if (el.tagName === 'A' && !el.getAttribute('role') && !el.closest('button') && !el.dataset.testid) {
      return;
    }

    playClick();
  };

  document.addEventListener('click', handler, { passive: true });
  return () => document.removeEventListener('click', handler);
}
